/*pagina pentru statusurile WAITING APPROVAL, APPROVED, REJECTED, ORDERED, PARTIALLY RECEIVED, RECEIVED, CANCELED*/
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import './PurchaseOrderView.css';
import axiosInstance from "../../../Config/axiosInstance";

const PurchaseOrderView = () => {
    const navigate = useNavigate();
    const {id} = useParams();
    const [showActions, setShowActions] = useState(false);
    const [selectedProductsList, setSelectedProductsList] = useState([]);
    const [comments, setComments] = useState([]);
    const [newComments, setNewComments] = useState("");
    const [user, setUser] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [systemCurrencyName, setSystemCurrencyName] = useState("EUR");
    const [systemCurrencyId, setSystemCurrencyId] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [formData, setFormData] = useState({
        order_number: "", 
        project_code: "",
        project_code_name: "", 
        subproject_code: "",
        subproject_code_name: "",
        supplier: "",
        supplier_name: "",
        supplier_address: "",
        arrival_deadline: "",
        payment_term: "",
        currency: "",
        currency_name: "",
        currency_abbreviation: "",
        exchange_rate: "",
        exchange_rate_value: null,
        exchange_rate_date: null,
        tax: "",
        tax_name: "",
        tax_value_percent: 0,
        note_to_supplier: "",
        status: "",
        total_wo_vat: "",
        vat_value: "",
        total: "",
        total_system_currency: "",
        delivery_address: "",
        delivery_address_name: "",
        delivery_address_full: "",
        order_sent_date: "",
        delivery_conditions: "",
        vat_value_tax: "",
        tax_value: "",
        comment: "",
        approval: false, 
    });

    const fetchUserProfile = async () => {
        try {
            const response = await axiosInstance.get('/user/name/');
            setUser(response.data);
            localStorage.setItem("user", JSON.stringify(response.data));
        } catch (error) {
            console.error('Error fetching profile: ', error);
        }
    };

    const fetchSystemCurrency = async () => {
        try {
            const response = await axiosInstance.get('/system-currency/1/');
            setSystemCurrencyId(response.data?.currency ?? null);
            if(response.data?.currency) {
                try {
                    const currencyResponse = await axiosInstance.get(`/currencies/${response.data.currency}/`);
                    setSystemCurrencyName(currencyResponse.data?.name || '');
                } catch (error) {
                    setSystemCurrencyName('')
                }
            }
        } catch (error) {
            console.error('Error fetching system currency: ', error);
            setSystemCurrencyName('');
        }
    };

    const fetchInvoices = useCallback(async () => {
        try {
            setLoadingInvoices(true);
            const cleanedId = String(id).replace(/\/$/, '');
            const response = await axiosInstance.get(`/puchase-order/${cleanedId}/invoice_numbers/`);
            setInvoices(response.data.invoice_numbers || []);
        } catch (error) {
            console.error("Error fetching invoices: ", error);
            setInvoices([]);
        } finally {
            setLoadingInvoices(false);
        }
    }, [id]);

    useEffect(() => {
        fetchSystemCurrency();
    }, []);

    useEffect(() => {
        const fetchPurchaseOrder = async () => {
            try {
                setIsLoading(true);
                const response = await axiosInstance.get(`/purchase-order/${id}/`);
                const po = response.data;

                setFormData({
                    supplier: po.supplier || "",
                    supplier_name: po.supplier_name || "",
                    supplier_address: po.supplier_address || "N/A",
                    order_number: po.order_number || "",
                    project_code: po.project_code || "",
                    project_code_name: po.project_code_name || "-",
                    subproject_code: po.subproject_code || "",
                    subproject_code_name: po.subproject_code_name || "-",
                    arrival_deadline: po.arrival_deadline || "",
                    payment_term: po.payment_term || "",
                    currency: po.currency || "",
                    currency_name: po.currency_name || "",
                    currency_abbreviation: po.currency_abbreviation || "",
                    exchange_rate: po.exchange_rate || "",
                    exchange_rate_value: po.exchange_rate_value || "",
                    exchange_rate_date: po.exchange_rate_date || "",
                    tax: po.tax || "",
                    tax_name: po.tax_name || "-",
                    tax_value_percent: po.tax_value_percent || 0,
                    note_to_supplier: po.note_to_supplier || "",
                    status: po.status || "",
                    total_wo_vat: po.total_wo_vat || "",
                    vat_value: po.vat_value || "",
                    total: po.total || "",
                    total_system_currency: po.total_system_currency || "",
                    delivery_address: po.delivery_address || "",
                    delivery_address_name: po.delivery_address_name || "-",
                    delivery_address_full: po.delivery_address_full || "-",
                    order_sent_date: po.order_sent_date || "",
                    delivery_conditions: po.delivery_conditions || "",
                    vat_value_tax: po.items?.[0]?.vat_value_tax || "",
                    approval: po.approval || false,
                    tax_value: po.tax_value || "",
                    comment: po.comment || "",
                });

                if(po.items && po.items.length > 0) {
                    const loadedItems = po.items.map(item => ({
                        id: item.product_detail?.id || item.product,
                        title: item.product_name || item.product_detail?.title || "",
                        product_id: item.product_detail?.id || item.product,
                        quantity: item.quantity,
                        vat: item.vat_value,
                        um: item.unit,
                        tax: item.tax_value || "",
                        tax_id: item.tax || "",
                        tax_name: item.tax_name || "",
                        cost: item.unit_price, 
                        purchase_order_item_id: item.id,
                        total: item.total,
                        total_system_currency: item.total_system_currency || 0,
                        arrival_deadline: item.arrival_deadline,
                        vat_value_tax: item.vat_value_tax,
                    }));
                    setSelectedProductsList(loadedItems);
                }

                const status = po.status;
                if(status === 'RECEIVED' || status === 'PARTIALLY RECEIVED') {
                    await fetchInvoices();
                }
            } catch (error) {
                console.error('Error fetching purchase order:', error);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchComments = async () => {
            try {
                const cleanedId = String(id).replace(/\/$/, '');
                const response = await axiosInstance.get(`/purchase-order-timeline/?purchase-order=${cleanedId}`);
                setComments(response.data);
            } catch (error) {
                console.error('Error fetching comments: ', error);
            }
        };
        if(id) {
            fetchPurchaseOrder();
            fetchComments();
            fetchUserProfile();
        }
    }, [id, fetchInvoices]);

    const userInitials = user?.first_name && user?.last_name
        ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
        : "U";

    const formatNumber = (value, locale = 'ro-RO') => {
        if(value === null || value === undefined || value === '') return '';
        const n = Number(value);
        if(Number.isNaN(n)) return '';
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(n);
    };

    const formatDateToRomanian = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    const formatDateShort = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleDateString('ro-RO', {month: 'short'});
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const normalizeDecimal = (value) => {
        if(value === null || value === undefined || value === "") return "";
        const number = parseFloat(value);
        if(isNaN(number)) return "";
        return new Intl.NumberFormat('ro-RO', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4
        }).format(number);
    };

    const getCurrentExchangeRate = () => {
        if(systemCurrencyId === parseInt(formData.currency)) {
            return 1;
        }
        return parseFloat(formData.exchange_rate_value);
    };

    const getExchangeRateDisplay = () => {
        if(systemCurrencyId === parseInt(formData.currency)) {
            return "Not needed (system currency)";
        }

        if(formData.exchange_rate_value) {
            const formattedValue = new Intl.NumberFormat('ro-RO', {
                minimumFractionDigits: 4,
                maximumFractionDigits: 4
            }).format(parseFloat(formData.exchange_rate_value));

            let dateDisplay = "";
            if(formData.exchange_rate_date) {
                dateDisplay = ` (${formatDateToRomanian(formData.exchange_rate_date)})`;
            }
            return `${formattedValue}${dateDisplay}`;
        }
        return "No exchange rate selected";
    }

    const isOrderedOrLater = () => {
        const status = formData.status;
        return ['ORDERED', 'RECEIVED', 'PARTIALLY RECEIVED', 'CANCELED', 'REVISED'].includes(status);
    };

    const isReceivedOrPartially = () => {
        const status = formData.status;
        return ['RECEIVED', 'PARTIALLY RECEIVED'].includes(status);
    };
    
    const showMarkAsOrdered = () => {
        if (formData.status === 'CANCELED') return false;
        return formData.approval === true && 
               formData.status !== "ORDERED" && 
               !['RECEIVED', 'PARTIALLY RECEIVED', 'REVISED'].includes(formData.status);
    };

    const showMoreActions = () => {
        const status = formData.status;
        return !['RECEIVED', 'PARTIALLY RECEIVED', 'CANCELED'].includes(status);
    };

    const handleMarkAsOrder = async () => {
        const updatedItems = selectedProductsList.map(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const cost = parseFloat(item.cost) || 0;
            const vat = parseFloat(item.vat) || 0;
            const total_wo_vat = quantity * cost;

            return {
                ...item,
                product: item.id,
                purchase_order: id,
                unit: item.um,
                quantity: quantity,
                unit_price: cost,
                tax: item.tax_id || formData.tax,
                total_wo_vat: parseFloat(total_wo_vat.toFixed(4)),
                vat_value: parseFloat(vat),
                total: parseFloat((parseFloat(item.total) || 0).toFixed(4)),
                status: 'ORDERED',
                id: item.purchase_order_item_id,
                vat_value_tax: item.vat_value_tax,
                total_system_currency: item.total_system_currency || 0
            };
        });

        const formattedFormData = {...formData};

        if(formattedFormData.order_sent_date) {
            const date = new Date(formattedFormData.order_sent_date);
            if(!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth()+1).padStart(2,'0');
                const day = String(date.getDate()).padStart(2, '0');
                formattedFormData.order_sent_date = `${year}-${month}-${day}`;
            }
        } else {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth()+1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            formattedFormData.order_sent_date = `${year}-${month}-${day}`;
        }

        const payload = {
            ...formattedFormData,
            status: 'ORDERED',
            items: updatedItems
        };

        try {
            await axiosInstance.put(`/purchase-order/${id}/`, payload);
            await axiosInstance.post(`/reception/create-or-update-from-po/${id}/`);
            setFormData(prev => ({...prev, status: 'ORDERED'}));
            setSelectedProductsList(updatedItems);
            navigate(-1);
        } catch (error) {
            console.error('Error: ', error.response?.data);
        }
    };

    const handleMarkAsCancel = async() => {
        const updatedItems = selectedProductsList.map(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const cost = parseFloat(item.cost) || 0;
            const vat = parseFloat(item.vat) || 0;
            const total_wo_vat = quantity * cost;

            return {
                ...item,
                product: item.id,
                purchase_order: id,
                unit: item.um,
                quantity: parseFloat(quantity),
                unit_price: parseFloat(cost),
                tax: item.tax_id || formData.tax,
                total_wo_vat: parseFloat(total_wo_vat.toFixed(4)),
                vat_value: parseFloat(vat),
                total: parseFloat((parseFloat(item.total) || 0).toFixed(4)),
                status:'CANCELED',
                id: item.purchase_order_item_id,
                vat_value_tax: item.vat_value_tax,
                total_system_currency: item.total_system_currency || 0,
            };
        });

        const payload = {
            ...formData,
            status:'CANCELED',
            items: updatedItems
        };

        try {
            await axiosInstance.put(`/purchase-order/${id}/`, payload);
            setFormData(prev => ({...prev, status: 'CANCELED'}));
            setSelectedProductsList(updatedItems);
            navigate(-1);
        } catch (error) {
            console.error('Error set status CANCELED: ', error.response);
        }
    };

    const handleEdit = () => {
        navigate(`/purchase-order/edit/${id}`);
    };

    const handleExportDocxRO = async () => {
        let timer;
        try {
            timer = setTimeout(() => setIsDownloading(true), 200);
            const response = await axiosInstance.get(`/genereaza_docx_procurare_ro/${id}/`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `oferta_${formData.order_number}.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.error(err);
            alert('Export error!');
        } finally {
            clearTimeout(timer);
            setIsDownloading(false);
        }
    };

    const handleExportDocxEN = async () => {
        let timer;
        try {
            timer = setTimeout(() => setIsDownloading(true), 200);
            const response = await axiosInstance.get(`/genereaza_docx_procurare_en/${id}/`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `offer_${formData.order_number}.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.error(err);
            alert('Export error!');
        } finally {
            clearTimeout(timer);
            setIsDownloading(false);
        }
    };

    const handlePostComment = async () => {
        if (!newComments?.trim()) return;

        try {
            const response = await axiosInstance.post(`/purchase-order-timeline/`, {
                purchase_order: id,
                comment: newComments
            });
            setComments([response.data, ...comments]);
            setNewComments("");
        } catch (error) {
            console.error("Error posting comment: ", error);
            if (error.response) {
                console.log("Server response:", error.response.data);
            }
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'RECEIVED':
                return 'view-status-badge view-status-received';
            case 'DRAFT':
                return 'view-status-badge view-status-draft';
            case 'ORDERED':
                return 'view-status-badge view-status-ordered';
            case 'CANCELED':
                return 'view-status-badge view-status-canceled';
            case 'WAITING APPROVAL':
                return 'view-status-badge view-status-wappr';
            case 'APPROVED':
                return 'view-status-badge view-status-approved';
            case 'REJECTED':
                return 'view-status-badge view-status-rejected';
            case 'PARTIALLY RECEIVED':
                return 'view-status-badge view-status-partially';
            case 'REVISED':
                return 'view-status-badge view-status-revised';
            default:
                return 'view-status-badge';
        }
    };

    const getStatusWithInfo = () => {
        if (formData.status === 'REJECTED' && formData.comment) {
            return (
                <div className="status-with-info">
                    <span className={getStatusClass(formData.status)}>
                        {formData.status}
                    </span>
                    <div className="info-tooltip-container">
                        <button className="info-button" aria-label="Rejection reason">
                            ℹ️
                        </button>
                        <div className="info-tooltip">
                            <strong>Reason:</strong><br />
                            {formData.comment}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <span className={getStatusClass(formData.status)}>
                {formData.status}
            </span>
        );
    };

    const renderProgressTimeline = () => {
        if (!formData.order_sent_date || !formData.arrival_deadline) return null;

        const sentDate = new Date(formData.order_sent_date);
        const arrivalDate = new Date(formData.arrival_deadline);
        const today = new Date();

        const totalDuration = (arrivalDate - sentDate) / (1000 * 60 * 60 * 24);
        const elapsed = (today - sentDate) / (1000 * 60 * 60 * 24);

        const isLate = today > arrivalDate;
        const progressPercent = isLate ? 100 : Math.min((elapsed / totalDuration) * 100, 100);
        const todayOffset = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);

        const labelRight = isReceivedOrPartially() ? "In stock / Expected arrival" : "Expected arrival";

        return (
            <div className="timeline-wrapper">
                <div className="timeline-bar-container">
                    <div className="label left">
                        <div>Order sent</div>
                        <strong>{formatDateShort(sentDate)}</strong>
                    </div>

                    <div className="timeline-bar">
                        <div
                            className="timeline-fill"
                            style={{
                                width: `${progressPercent}%`,
                                backgroundColor: isLate ? '#e53935' : '#4caf50'
                            }}
                        ></div>

                        {!isLate && (
                            <div className="timeline-today" style={{ left: `${todayOffset}%` }}>
                                <div className="today-label">
                                    <span>{formatDateShort(today)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="label right">
                        <div>{labelRight}</div>
                        <strong>{formatDateShort(arrivalDate)}</strong>
                    </div>
                </div>

                {isLate && (
                    <p style={{ color: '#e53935', marginTop: '8px', fontSize: '12px' }}>
                        The delivery deadline has been exceeded!
                    </p>
                )}
            </div>
        );
    };

    const renderLoader = () => (
        <div className="loader-overlay">
            <div className="loader-container">
                <div className="spinner-large"></div>
                <p className="loader-text">Loading data...</p>
            </div>
        </div>
    );

    const renderInvoices = () => {
        if (!isReceivedOrPartially()) return null;
        
        return (
            <div>
                <h4>INVOICE</h4>
                {loadingInvoices ? (
                    <p className='details'>Loading invoices...</p>
                ) : invoices.length > 0 ? (
                    <div className="invoices-list">
                        {invoices.map((invoiceNumber, index) => (
                            <div key={index} className="invoice-item">
                                <p className='details'>{invoiceNumber}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className='details' style={{ color: '#999' }}>No invoices</p>
                )}
            </div>
        );
    };

    return (
        <div className="view-po-page"> 
            {isLoading && renderLoader()}

            {isDownloading && (
                <div className="download-toast"> 
                    <div className="spinner"></div>
                    Please wait, downloading...
                </div>
            )}

            <div className="view-po-container">
                {/*header*/}
                <div className="view-header header-flex">
                    <div className="left-section">
                        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)}/>
                        <div className="title-status-wrapper">
                            <h2 className="view-po-title">
                                {formData.order_number}
                            </h2>
                            {getStatusWithInfo()}
                        </div>
                    </div>

                    <div className="right-section">
                        {showMoreActions() && (
                            <div className="dropdown-container">
                                <button className="btn btn-secondary" onClick={() => setShowActions(prev => !prev)}>
                                    More Actions {showActions ? '▲' : '▼'}
                                </button>
                                {showActions && (
                                    <div className="dropdown-menu">
                                        <div className="dropdown-item" onClick={handleMarkAsCancel}>Cancel order</div>
                                        <div className="dropdown-item" onClick={handleEdit}>Edit</div>
                                        <div className="dropdown-item" onClick={handleExportDocxRO}>Export doc. RO</div>
                                        <div className="dropdown-item" onClick={handleExportDocxEN}>Export doc. EN</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {showMarkAsOrdered() && (
                            <button className="btn btn-primary" onClick={handleMarkAsOrder}>
                                Mark as Ordered
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="view-po-container">
                <div className="card custom-card">
                    <div className="grid-3">
                        <div>
                            <h4>SUPPLIER</h4>
                            <p className="supplier-address"><strong>{formData.supplier_name}</strong></p>
                            <p className="supplier-address">{formData.supplier_address}</p>
                        </div>
                        <div>
                            <h4>DELIVERY ADDRESS</h4>
                            <p className="delivery-address"><strong>{formData.delivery_address_name}</strong></p>
                            <p className="delivery-address">{formData.delivery_address_full}</p>
                            <h4>DELIVERY CONDITIONS</h4>
                            <p className="delivery-address">{formData.delivery_conditions}</p>
                        </div>
                        <div>
                            <h4>PROJECT CODE</h4>
                            <p className="project-code"><strong>{formData.project_code_name}</strong></p>
                            <h4 style={{marginTop: '20px'}}>SUB-PROJECT CODE</h4>
                            <p className="project-code"><strong>{formData.subproject_code_name}</strong></p>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className={isReceivedOrPartially() ? 'grid-4' : 'grid-3'}>
                        <div>
                            <h4>SHIPMENT DETAILS</h4>
                            <label>Arrival Deadline</label>
                            <p className="details">{formatDateToRomanian(formData.arrival_deadline)}</p>
                        </div>
                        <div>
                            <h4>PAYMENT</h4>
                            <label>Payment Term</label>
                            <p className="details">{formData.payment_term} days</p>
                        </div>
                        {isReceivedOrPartially() && (
                            <div>
                                {renderInvoices()}
                            </div>
                        )}
                        <div>
                            <h4>CURRENCY & EXCHANGE RATE</h4>
                            <label>Currency selected</label>
                            <p className="details">{formData.currency_name}</p>
                            <label style={{marginTop: '10px'}}>Exchange Rate</label>
                            <p className="details">{getExchangeRateDisplay()}</p>
                        </div>
                    </div>
                </div>

                {isOrderedOrLater() && (
                    <div className="card">
                        {renderProgressTimeline()}
                    </div>
                )}

                <div className="card">
                    <h4>PRODUCTS</h4>
                    <div className="table-responsive">
                        <table className="po-items-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Quantity</th>
                                    <th>UM</th>
                                    <th>VAT</th>
                                    <th>Price per unit</th>
                                    <th>Total ({formData.currency_name})</th>
                                    <th>System Total ({systemCurrencyName})</th>
                                    <th>Arrival Deadline</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedProductsList.map(item => (
                                    <tr key={item.purchase_order_item_id}>
                                        <td style={{ color: '#0831eaff', fontWeight: 'bold' }}>{item.title}</td>
                                        <td>{normalizeDecimal(item.quantity)}</td>
                                        <td>{item.um}</td>
                                        <td>{normalizeDecimal(item.vat)}</td>
                                        <td>{normalizeDecimal(item.cost)}</td>
                                        <td>{formatNumber(item.total)} <b>{formData.currency_name}</b></td>
                                        <td>{formatNumber(item.total_system_currency) || 0} <b>{systemCurrencyName}</b></td>
                                        <td>{formatDateToRomanian(item.arrival_deadline)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan={3}>
                                        <span><b>Taxes - {formData.tax_name}</b></span>
                                    </td>
                                    <td>
                                        {normalizeDecimal(formData.tax_name)}
                                    </td>
                                    <td>
                                        {normalizeDecimal(formData.tax_value)}
                                    </td>
                                    <td>
                                        {(() => {
                                            const vatValueTax = parseFloat(formData.vat_value_tax);
                                            const taxValue = parseFloat(formData.tax_value)
                                            if(isNaN(vatValueTax) || isNaN(taxValue)) {
                                                return "N/A"
                                            }
                                            const result = taxValue + (taxValue * vatValueTax) / 100;
                                            return (
                                                <>
                                                {formatNumber(result)} <b>{formData.currency_name}</b>
                                                </>
                                            );
                                        })()}
                                    </td>
                                    <td>
                                        {(() => {
                                            const vatValueTax = parseFloat(formData.vat_value_tax);
                                            const taxValue = parseFloat(formData.tax_value);
                                            if(isNaN(vatValueTax) || isNaN(taxValue)) {
                                                return "N/A";
                                            }
                                            const taxOnly = taxValue;
                                            const taxVat = (taxValue * vatValueTax) / 100;
                                            const deliveryTaxOriginal = taxOnly + taxVat;
                                            const exchangeRate = getCurrentExchangeRate();
                                            const deliveryTaxSystem = deliveryTaxOriginal * exchangeRate;
                                            return (
                                                <>
                                                {formatNumber(deliveryTaxSystem)} <b>{systemCurrencyName}</b>
                                                </>
                                            );
                                        })()}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{fontSize: '10px', fontFamily: 'Arial, sans-serif'}} colSpan={8}>
                                        {selectedProductsList.length} variants on purchase order
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="grid-2">
                    <div className="card">
                        <h4>ADDITIONAL DETAILS</h4>
                        <h4>Note to supplier</h4>
                        <hr/>
                        <p>{formData.note_to_supplier}</p>
                    </div>

                    <div className="card">
                        <h4>COST SUMMARY</h4>
                        {(() => {
                            const currencyLabel = formData.currency_name;
                            const systemCurrencyLabel = systemCurrencyName;
                            const exchangeRate = getCurrentExchangeRate();

                            const vatValueTax = parseFloat(formData.vat_value_tax) || 0;
                            const taxValue = parseFloat(formData.tax_value) || 0;
                            
                            const taxOnly = taxValue;
                            const taxVat = (taxValue * vatValueTax)/100;

                            const taxOnlySystem = taxOnly * exchangeRate;
                            const taxVatSystem = taxVat * exchangeRate;

                            const totalWithoutVat = parseFloat(formData.total_wo_vat) || 0;
                            const totalWithoutVatSystem = totalWithoutVat * exchangeRate;

                            const productVat = parseFloat(formData.vat_value) || 0;
                            const productVatSystem = productVat * exchangeRate;

                            const totalVat = productVat + taxVat;
                            const totalVatSystem = productVatSystem + taxVatSystem;

                            const totalGeneral = parseFloat(formData.total) || 0;
                            const totalGeneralSystem = totalGeneral * exchangeRate;

                            const formatValue = (value) => {
                                return Number(value).toLocaleString('ro-RO', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                });
                            };

                            return (
                                <div className="summary-container">
                                    <table className="summary-table">
                                        <thead>
                                            <tr className="table-header">
                                                <th className="cell-left"></th>
                                                <th className="header-currency">Total Currency</th>
                                                <th className="arrow-column"></th>
                                                <th className="header-system">System Total ({systemCurrencyLabel})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="table-row">
                                                <td className="cell-left label">Total without VAT</td>
                                                <td className="cell-right">
                                                    {formatValue(totalWithoutVat)} {currencyLabel}
                                                </td>
                                                <td className="arrow-column">
                                                    <span className="arrow-icon">›</span>
                                                </td>
                                                <td className="cell-right system-value">
                                                    {formatValue(totalWithoutVatSystem)} {systemCurrencyLabel}
                                                </td>
                                            </tr>

                                            <tr className="table-row">
                                                <td className="cell-left label">Taxes without VAT</td>
                                                <td className="cell-right">
                                                    {formatValue(taxOnly)} {currencyLabel}
                                                </td>
                                                <td className="arrow-column">
                                                    <span className="arrow-icon">›</span>
                                                </td>
                                                <td className="cell-right system-value">
                                                    {formatValue(taxOnlySystem)} {systemCurrencyLabel}
                                                </td>
                                            </tr>

                                            <tr className="table-row">
                                                <td className="cell-left label">VAT</td>
                                                <td className="cell-right">
                                                    {formatValue(totalVat)} {currencyLabel}
                                                </td>
                                                <td className="arrow-column">
                                                    <span className="arrow-icon">›</span>
                                                </td>
                                                <td className="cell-right system-value">
                                                    {formatValue(totalVatSystem)} {systemCurrencyLabel}
                                                </td>
                                            </tr>

                                            <tr className="table-row total-row">
                                                <td className="cell-left total-label">Total</td>
                                                <td className="cell-right total-value">
                                                    {formatValue(totalGeneral)} {currencyLabel}
                                                </td>
                                                <td className="arrow-column">
                                                    <span className="arrow-icon">›</span>
                                                </td>
                                                <td className="cell-right total-system-value">
                                                    <strong>{formatValue(totalGeneralSystem)} {systemCurrencyLabel}</strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                <div className="card">
                    <h4 className="timeline-title">Timeline</h4>
                    <div className="comment-new">
                        <div className="comment-input-area">
                            <div className="avatar">{userInitials}</div>
                            <div className="input-block">
                                <textarea
                                    placeholder="Leave a comment..."
                                    value={newComments}
                                    onChange={(e) => setNewComments(e.target.value)}
                                />
                                <p className="comment-note">
                                    Only you and other staff can see comments.
                                </p>
                            </div>
                        </div>
                        <div className="post-btn-area">
                            <button
                                className="post-button"
                                onClick={handlePostComment}
                                disabled={!newComments?.trim()}
                            >
                                POST
                            </button>
                        </div>
                    </div>

                    {comments.map((c) => (
                        <div key={c.id} className="comment-item">
                            <div className="avatar">{c.user_initials}</div>
                            <div className="comment-content">
                                <div className="comment-header">
                                    <span className="user-name">{c.user_name}</span>
                                    <span className="comment-date">
                                        {new Date(c.created_at).toLocaleDateString("ro-RO", {
                                            day: '2-digit',
                                            month: "short",
                                            year: "numeric"
                                        })}
                                    </span>
                                </div>
                                <p className="comment-text">{c.comment}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderView;